/**
 * Mini Cart Component
 * Handles hover behavior and cart updates for the mini cart
 */
export class MiniCart {
  constructor(cartContainer = null) {
    this.root = cartContainer;
    this.container = null;
    this.cartIcon = null;
    this.cartElements = [];
    this.isVisible = false;
    this.hoverTimeout = null;
    this.showTimeout = null;
    
    // Debounce timers for each item
    this.debounceTimers = new Map();
    // Track pending quantity changes for each item
    this.pendingChanges = new Map();
    // Track which items are currently being updated (per-item state)
    this.updatingItems = new Set();

    // NEW: track last committed values to avoid redundant cart updates
    this.lastCommittedQty = new Map(); // key -> number
    
    // Connection health monitoring
    this.connectionHealth = {
      isSlowConnection: false,
      lastResponseTime: null,
      slowResponseCount: 0
    };
    
    // A/B Testing support
    // Set window.abTestVariant to 'variant1' or 'variant2' to enable A/B testing
    // Note: We check window.abTestVariant dynamically, so it works even if set after initialization
    this.lastScrollY = window.scrollY || 0;
    this.clickCount = 0; // Track clicks for Variant 2
    this.isScrollingMiniCart = false; // Track if user is scrolling inside mini cart
    
    this.init();
  }
  
  /**
   * Get current A/B test variant (checks window.abTestVariant dynamically)
   * This allows the variant to be set after component initialization
   */
  getAbTestVariant() {
    return window.abTestVariant || null;
  }

  isElementVisible(element) {
    if (!element) return false;

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }

    return element.getClientRects().length > 0;
  }

  init() {
    console.log("Mini cart initiated")
    const cartContainers = this.root
      ? [this.root]
      : Array.from(document.querySelectorAll('[data-cart-container]'));

    this.cartIcon = cartContainers.find((container) => {
      const miniCartEnabled = container.dataset.miniCartEnabled === 'true';
      return miniCartEnabled && !!container.querySelector('[data-mini-cart]');
    }) || null;

    this.container = this.cartIcon?.querySelector('[data-mini-cart]') || null;
    
    if (!this.container || !this.cartIcon) {
      console.warn('MiniCart: Required elements not found');
      return;
    }

    const cartElements = Array.from(
      this.cartIcon.querySelectorAll('[data-cart-toggle], [data-cart-link]')
    );
    
    if (!cartElements.length) {
      console.log('MiniCart: No cart icon found (neither button nor link)');
      return;
    }
    
    // Store references to all cart elements for click and hover events
    this.cartElements = cartElements;
    this.cartElement = this.cartElements[0];

    this.bindEvents();
    this.setupCartEventListeners();
    
    // Add manual test methods to window for debugging
    window.testMiniCart = () => {
      console.log('MiniCart: Manual test triggered');
      this.showOnAddToCart();
    };
    
    window.testMiniCartEvents = () => {
      console.log('MiniCart: Testing cart events manually');
      // Simulate cart:added event
      const mockEvent = new CustomEvent('cart:added', {
        detail: { cart: { item_count: 1, total_price: 1000 } }
      });
      document.dispatchEvent(mockEvent);
    };
    
    // Check if there's already cart data and update immediately
    setTimeout(() => {
      if (window.cartManager && window.cartManager.cart) {
        console.log('MiniCart: Found existing cart data, updating immediately');
        // this.updateMiniCart(window.cartManager.cart);
      } else {
        // Initialize checkout button visibility for empty cart
        console.log('MiniCart: No cart data, hiding checkout button');
        this.hideCheckoutButton();
      }
      
      // // Also try to listen directly to the CartManager instance
      if (window.cartManager && typeof window.cartManager.on === 'function') {
        console.log('MiniCart: Setting up direct CartManager event listeners');
        window.cartManager.on('cart:added', this.handleCartAdded);
        window.cartManager.on('cart:updated', this.handleCartUpdated);
        window.cartManager.on('cart:loaded', this.handleCartLoaded);
        window.cartManager.on('cart:cleared', this.handleCartCleared);
      }
    }, 100);
    
    // Add a method to manually trigger MiniCart from cart events
    window.triggerMiniCartOnAdd = () => {
      console.log('MiniCart: Manual trigger from cart event');
      if (this.isMiniCartEnabled()) {
        this.showOnAddToCart();
      }
    };
    
    console.log('MiniCart: Initialized - you can test with window.testMiniCart() or window.testMiniCartEvents()');
  }

  isMiniCartEnabled() {
    // Check if mini cart is enabled via header settings
    const enabled = this.cartIcon?.dataset.miniCartEnabled === 'true';
    console.log('MiniCart: isMiniCartEnabled check:', {
      cartIcon: !!this.cartIcon,
      dataset: this.cartIcon?.dataset,
      miniCartEnabled: this.cartIcon?.dataset.miniCartEnabled,
      result: enabled
    });
    return enabled;
  }

  bindEvents() {
    this.cartElements.forEach((cartElement) => {
      cartElement.addEventListener('click', (event) => {
        // A/B Testing Variant 2: Desktop redirects to cart, Mobile toggles mini cart
        // Check dynamically in case variant is set after initialization
        if (this.getAbTestVariant() === 'variant2') {
          event.preventDefault();
          
          if (window.innerWidth >= 1000) {
            // Desktop: Always redirect to cart page (same as window.cartRedirectToPage = true)
            window.location.href = '/cart';
            return;
          } else {
            // Mobile: Toggle behavior - close if open, open if closed
            if (this.isVisible) {
              this.hide();
            } else {
              this.showOnClick();
            }
            return;
          }
        }
        
        // Legacy cartRedirectToPage behavior (for backward compatibility)
        if (window.cartRedirectToPage === true) {
          event.preventDefault();
          window.location.href = '/cart';
          return;
        }
        
        // A/B Testing Variant 1: Toggle mini cart on click (hover still works)
        // Check dynamically in case variant is set after initialization
        if (this.getAbTestVariant() === 'variant1') {
          event.preventDefault();
          
          if (this.isVisible) {
            this.hide();
          } else {
            this.showOnClick();
          }
          return;
        }
        
        // Default behavior
        event.preventDefault();
        this.showOnClick();
      });

      // Hover behavior works for all variants including Variant 1
      cartElement.addEventListener('mouseenter', () => {
        this.showOnHover();
      });

      cartElement.addEventListener('mouseleave', () => {
        this.hideOnHover();
      });
    });

    // Prevent mini cart from hiding when hovering over it
    this.container.addEventListener('mouseenter', () => {
      this.clearHideTimeout();
      this.clearAutoHideTimeout();
    });

    this.container.addEventListener('mouseleave', () => {
      this.hideOnHover();
    });

    // Handle quantity and remove buttons
    this.container.addEventListener('click', (event) => {
      this.clearAutoHideTimeout();
      this.handleCartActions(event);
    });

    // ✅ Quantity input live editing (debounced change on input)
    this.container.addEventListener('input', (event) => {
      this.clearAutoHideTimeout();
      this.handleQuantityInputChange(event, { commit: false });
    });

    // ✅ Change event (spinner arrows / some browsers commit)
    this.container.addEventListener('change', (event) => {
      this.clearAutoHideTimeout();
      this.handleQuantityInputChange(event, { commit: true });
    });

    // ✅ Immediate commit on blur and Enter key
    this.container.addEventListener('blur', (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.matches('[data-quantity-input]')) return;
      this.handleQuantityInputChange({ target: event.target }, { commit: true });
    }, true);

    this.container.addEventListener('keydown', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.matches('[data-quantity-input]')) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        this.handleQuantityInputChange({ target }, { commit: true });
      }
    });

    // Click outside to close mini cart - capture
    document.addEventListener('click', (event) => {
      this.handleClickOutside(event);
    }, true);

    // Touch outside to close (gallery uses preventDefault on touch)
    document.addEventListener('touchstart', (event) => {
      this.handleClickOutside(event);
    }, true);

    // Backup listeners
    document.body.addEventListener('click', (event) => {
      this.handleClickOutside(event);
    }, true);
    document.body.addEventListener('touchstart', (event) => {
      this.handleClickOutside(event);
    }, true);
    
    // A/B Testing Variant 1: Hide on scroll up
    // Check dynamically in case variant is set after initialization
    this.setupVariant1Listeners();
    
    // Handle continue shopping click for Variant 1
    this.setupVariant1ClickHandlers();
  }

  handleClickOutside(event) {
    // Only close if mini cart is visible
    if (!this.isVisible) return;
    
    const isClickInsideMiniCart = this.container && this.container.contains(event.target);
    const isClickOnCartIcon = this.cartElements.some((cartElement) => cartElement.contains(event.target));
    const isClickOnMiniCartElement = event.target.closest?.('[data-mini-cart]');
    const isClickOnGallery = event.target.closest?.('[data-main-gallery], [data-gallery-track], [data-thumbnail-gallery], [data-thumbnail-track], [data-thumbnail-button], [data-gallery-prev], [data-gallery-next]');
    
    // A/B Testing Variant 1: Hide on click outside
    // Check dynamically in case variant is set after initialization
    if (this.getAbTestVariant() === 'variant1') {
      if ((!isClickInsideMiniCart && !isClickOnCartIcon && !isClickOnMiniCartElement) || isClickOnGallery) {
        this.hide();
      }
      return;
    }
    
    // Default behavior
    if ((!isClickInsideMiniCart && !isClickOnCartIcon && !isClickOnMiniCartElement) || isClickOnGallery) {
      this.hide();
    }
  }
  
  /**
   * Handle scroll event for Variant 1 and Variant 2 - hide on any scroll outside mini cart
   */
  handleScroll() {
    if (!this.isVisible) return;
    
    // Don't close if user is currently scrolling inside the mini cart
    if (this.isScrollingMiniCart) {
      return;
    }
    
    const miniCartElement = this.container;
    if (!miniCartElement) return;
    
    // Check if mouse is over the mini cart (user might be scrolling inside)
    const mouseOverMiniCart = miniCartElement.matches(':hover');
    if (mouseOverMiniCart) {
      // Check if mini cart content is scrollable and being scrolled
      const miniCartContent = miniCartElement.querySelector('.mini-cart__content');
      if (miniCartContent) {
        const isContentScrollable = miniCartContent.scrollHeight > miniCartContent.clientHeight;
        if (isContentScrollable) {
          // User is hovering over mini cart and it's scrollable
          // Don't close on small scroll changes (might be mini cart scroll)
          const currentScrollY = window.scrollY || window.pageYOffset;
          const scrollDelta = Math.abs(currentScrollY - this.lastScrollY);
          
          // If scroll change is small (< 10px), it might be mini cart internal scroll
          // Don't close in this case
          if (scrollDelta < 10) {
            this.lastScrollY = currentScrollY;
            return;
          }
        }
      }
    }
    
    // Close on any scroll (up or down) outside the mini cart
    const currentScrollY = window.scrollY || window.pageYOffset;
    
    // Only close if scroll position actually changed (to avoid false triggers)
    if (currentScrollY !== this.lastScrollY) {
      this.hide();
    }
    
    this.lastScrollY = currentScrollY;
  }
  
  /**
   * Handle continue shopping click for Variant 1
   */
  handleContinueShopping() {
    // Check if we're on a product page or collection page
    // Try multiple methods for reliable detection
    const pathname = window.location.pathname.toLowerCase();
    const fullUrl = window.location.href.toLowerCase();
    
    // Product page detection - must be a single product page (NOT a collection)
    // Product pages are: /products/product-name (NOT /collections/products)
    // Check collection first to avoid false positives
    const isCollectionPage = 
      document.body.classList.contains('template-collection') || 
      pathname.startsWith('/collections/') ||
      fullUrl.includes('/collections/');
    
    const isProductPage = 
      !isCollectionPage && ( // Only check if NOT a collection page
        document.body.classList.contains('template-product') || 
        pathname.startsWith('/products/') ||
        document.querySelector('[data-product-form]') ||
        document.querySelector('form[action*="/cart/add"]')
      );
    
    console.log('MiniCart A/B Test: Continue Shopping clicked', {
      isProductPage,
      isCollectionPage,
      pathname,
      fullUrl,
      bodyClass: document.body.className
    });
    
    if (isProductPage) {
      // Redirect to specific collection URL
      window.location.href = 'https://spadeandco.com/collections/bands-for-health-smartwatch-4';
    } else if (isCollectionPage) {
      // Just dismiss the mini cart (no redirect)
      console.log('MiniCart A/B Test: Collection page detected, dismissing mini cart');
      this.hide();
    } else {
      // Default: dismiss mini cart
      console.log('MiniCart A/B Test: Unknown page type, dismissing mini cart');
      this.hide();
    }
  }

  /**
   * Setup Variant 1 and Variant 2 scroll listener (checks dynamically)
   */
  setupVariant1Listeners() {
    // Use a single scroll handler that checks the variant dynamically
    let scrollHandlerAdded = false;
    
    const scrollHandler = () => {
      const variant = this.getAbTestVariant();
      // Handle scroll for both Variant 1 and Variant 2
      if (variant === 'variant1' || variant === 'variant2') {
        this.handleScroll();
      }
    };
    
    // Add scroll listener once
    if (!scrollHandlerAdded) {
      // Listen to scroll events on window (page scroll)
      window.addEventListener('scroll', scrollHandler, { passive: true });
      
      // Listen to scroll events on mini cart content to detect internal scrolling
      // This prevents closing when user scrolls inside the mini cart
      const setupMiniCartScrollListener = () => {
        const miniCartContent = this.container?.querySelector('.mini-cart__content');
        if (miniCartContent) {
          miniCartContent.addEventListener('scroll', () => {
            // Mark that user is scrolling inside mini cart
            this.isScrollingMiniCart = true;
            
            // Reset flag after a short delay
            clearTimeout(this.miniCartScrollTimeout);
            this.miniCartScrollTimeout = setTimeout(() => {
              this.isScrollingMiniCart = false;
            }, 150);
          }, { passive: true });
        }
      };
      
      // Setup listener immediately and also after a delay (in case container isn't ready)
      setupMiniCartScrollListener();
      setTimeout(setupMiniCartScrollListener, 500);
      
      scrollHandlerAdded = true;
    }
  }
  
  /**
   * Setup Variant 1 and Variant 2 click handlers (checks dynamically)
   */
  setupVariant1ClickHandlers() {
    // Handle continue shopping and checkout clicks for Variant 1 and Variant 2
    // The handler checks dynamically each time, so it works even if variant is set late
    this.container.addEventListener('click', (event) => {
      const variant = this.getAbTestVariant();
      
      // Handle Continue Shopping for both Variant 1 and Variant 2
      const continueShoppingLink = event.target.closest('a[data-continue-shopping]');
      if (continueShoppingLink && (variant === 'variant1' || variant === 'variant2')) {
        event.preventDefault();
        event.stopPropagation(); // Prevent any other handlers
        event.stopImmediatePropagation(); // Stop all handlers on this element
        
        // Remove href temporarily to prevent navigation
        const originalHref = continueShoppingLink.href;
        continueShoppingLink.href = 'javascript:void(0)';
        
        // Call handler
        this.handleContinueShopping();
        
        // Restore href after a short delay (in case handler redirects)
        setTimeout(() => {
          if (continueShoppingLink.href === 'javascript:void(0)') {
            continueShoppingLink.href = originalHref;
          }
        }, 100);
        
        return false; // Additional prevention
      }
      
      // Hide mini cart when checkout button is clicked (Variant 1)
      if (variant === 'variant1') {
        const checkoutButton = event.target.closest('[data-checkout-button]');
        if (checkoutButton) {
          // Let the form submit, but hide mini cart
          setTimeout(() => {
            this.hide();
          }, 100);
        }
      }
    });
  }
  
  // Fallback method to ensure mini cart closes - can be called manually
  forceCloseOnOutsideClick() {
    if (!this.isVisible) return;
    setTimeout(() => {
      if (this.isVisible) {
        this.hide();
      }
    }, 100);
  }

  clearAutoHideTimeout() {
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
      this.autoHideTimeout = null;
    }
  }

  setupCartEventListeners() {
    console.log('MiniCart: Setting up cart event listeners...');
    
    // Create bound event handlers to avoid issues with 'this' context
    this.handleCartUpdated = (event) => {
      const cart = event.detail?.cart;
      console.log('[MiniCart] handleCartUpdated (DOM)', { hasCart: !!cart, item_count: cart?.item_count });
      if (!cart) return;
      // Single source of truth: always replace list with event cart (avoids stale/ghost items)
      this.updateCartItems(Array.isArray(cart.items) ? cart.items : []);
      this.updateMiniCart(cart, event.detail?.sections);
    };

    this.handleCartAdded = (event) => {
      console.log('MiniCart: Received cart:added event', event.detail);
      
      // Auto-show mini cart when items are added (if mini cart is enabled)
      if (this.isMiniCartEnabled()) {
        this.showOnAddToCart();
        // Ensure checkout button is shown when items are added
        this.showCheckoutButton();
      }
      
      // Update cart data after ensuring checkout button is visible
      const cart = event.detail?.cart;
      
      // Always update cart items to properly detect _for property and disable controls
      if (cart && cart.items) {
        console.log('MiniCart: Updating cart items after add:', cart.items.length, 'items');
        // Force update all items to ensure _for items are disabled
        this.updateCartItems(cart.items);
      }
      
      this.updateMiniCart(cart, event.detail?.sections);
    };

    this.handleCartLoaded = (event) => {
      const cart = event.detail?.cart;
      console.log('[MiniCart] handleCartLoaded (DOM)', { hasCart: !!cart, item_count: cart?.item_count });
      this.updateMiniCart(cart);
    };

    this.handleCartCleared = (event) => {
      console.log('MiniCart: Received cart:cleared event', event.detail);
      this.updateMiniCart(event.detail?.cart);
    };

    this.handleCartEmptied = (event) => {
      console.log('MiniCart: Received cart:emptied event', event.detail);
      this.handleEmptyCart();
    };
   
    
    const listenerTarget = typeof window !== 'undefined' && window.dispatchEvent ? window : document;
    console.log(listenerTarget)
    // Listen for cart updates from the unified cart system
    listenerTarget.addEventListener('cart:updated', this.handleCartUpdated);
    listenerTarget.addEventListener('cart:added', this.handleCartAdded);
    listenerTarget.addEventListener('cart:loaded', this.handleCartLoaded);
    listenerTarget.addEventListener('cart:cleared', this.handleCartCleared);
    listenerTarget.addEventListener('cart:emptied', this.handleCartEmptied);
    
    
    // Add a global event listener that catches all cart events for debugging
    const debugHandler = (event) => {
      if (event.type.startsWith('cart:')) {
        console.log('MiniCart: Global cart event detected:', event.type, event.detail);
      }
    };
    document.addEventListener('cart:updated', debugHandler);
    document.addEventListener('cart:added', debugHandler);
    document.addEventListener('cart:loaded', debugHandler);
    document.addEventListener('cart:cleared', debugHandler);
    
    console.log('MiniCart: Cart event listeners set up successfully');
  }

  showOnHover() {
    this.clearHideTimeout();
    this.clearShowTimeout();
    
    // Small delay to prevent accidental shows
    this.showTimeout = setTimeout(() => {
      this.show();
    }, 150);
  }

  hideOnHover() {
    this.clearShowTimeout();
    
    // Delay hiding to allow moving mouse to mini cart
    this.hoverTimeout = setTimeout(() => {
      this.hide();
    }, 300);
  }

  showOnAddToCart() {
    this.showCheckoutButton();
    this.show();
    this.clearAutoHideTimeout();
    
    // A/B Testing Variant 1: No timeout, cart stays visible
    // Check dynamically in case variant is set after initialization
    if (this.getAbTestVariant() === 'variant1') {
      // Don't set auto-hide timeout for variant 1
      console.log('MiniCart A/B Test: Variant 1 - Mini cart shown, no auto-hide timeout');
      return;
    }
    
    // A/B Testing Variant 2: Desktop only - show with timeout
    // Check dynamically in case variant is set after initialization
    if (this.getAbTestVariant() === 'variant2' && window.innerWidth >= 1000) {
      this.autoHideTimeout = setTimeout(() => {
        // Don't auto-hide if there are pending quantity changes or cart updates
        if (this.pendingChanges.size > 0 || this.isUpdatingCart) {
          return;
        }
        this.hide();
      }, 4000);
      return;
    }
    
    // Default behavior: auto-hide after 4 seconds
    this.autoHideTimeout = setTimeout(() => {
      // Don't auto-hide if there are pending quantity changes or cart updates
      if (this.pendingChanges.size > 0 || this.isUpdatingCart) {
        return;
      }
      this.hide();
    }, 4000);
  }

  showOnClick() {
    this.show();
    if (window.cartManager?.cart?.item_count > 0) {
      this.showCheckoutButton();
    }
    
    // A/B Testing Variant 2: Desktop only - set timeout when showing on click
    // Check dynamically in case variant is set after initialization
    if (this.getAbTestVariant() === 'variant2' && window.innerWidth >= 1000) {
      this.clearAutoHideTimeout();
      this.autoHideTimeout = setTimeout(() => {
        if (this.pendingChanges.size > 0 || this.isUpdatingCart) {
          return;
        }
        this.hide();
      }, 4000);
    }
  }

  show() {
    if (this.isVisible) {
      return;
    }
    
    this.container.classList.add('is-visible');
    this.isVisible = true;
    
    // Update cart data
    // this.updateMiniCart();
  }

  hide() {
    if (!this.isVisible) return;
    
    this.container.classList.remove('is-visible');
    this.isVisible = false;
    
    // Clear auto-hide timeout when manually hiding
    this.clearAutoHideTimeout();
  }

  clearShowTimeout() {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
  }

  clearHideTimeout() {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }

  showCheckoutButton() {
    const checkoutFooter = this.container.querySelector('.mini-cart__footer');
    if (checkoutFooter) {
      checkoutFooter.style.display = 'block';
    }
  }

  hideCheckoutButton() {
    const checkoutFooter = this.container.querySelector('.mini-cart__footer');
    if (checkoutFooter) {
      checkoutFooter.style.display = 'none';
    }
  }

  updateMiniCart(cartData = null, sections = null) {
    if (cartData == null && sections == null) {
      return
    }
    console.log("Update Mini Cart");
    console.log(sections)
    if (!cartData) {
      // Try to get cart from unified cart system
      if (window.cartManager) {
        cartData = window.cartManager.cart;
      }
    }

    // if (!cartData) return;
    
    if (sections != null) {
      const parser = new DOMParser();
      const updatedHeader = parser.parseFromString(Object.values(sections)[0], 'text/html'); //change to loop, for now only header
      console.log(updatedHeader)
      const updatedCount = parseInt(updatedHeader.querySelector('[data-cart-count]').textContent);
      const updatedCart = updatedHeader.querySelector('[data-mini-cart]');
      console.log(updatedCount)

      // Close after 4s if cart becomes empty while visible
      // A/B Testing Variant 1: Don't auto-hide even when cart becomes empty
      // Check dynamically in case variant is set after initialization
      if (updatedCount === 0 && this.isVisible && this.getAbTestVariant() !== 'variant1') {
        this.clearAutoHideTimeout();
        this.autoHideTimeout = setTimeout(() => this.hide(), 4000);
      }

      // Checkout button visibility
      if (updatedCount === 0) {
        this.hideCheckoutButton();
        // Ensure empty state is shown
        this.updateCartItems([]);
      } else {
        this.showCheckoutButton();
      }

      // Update checkout button state
      const checkoutButton = this.container.querySelector('[name="checkout"]');
      if (checkoutButton) {
        checkoutButton.disabled = updatedCount === 0;
      }

      // Mini Cart Count
      const globalCountElements = document.querySelectorAll('[data-cart-count]');
      globalCountElements.forEach(element => {
        element.textContent = updatedCount;
        element.setAttribute('data-count', updatedCount);
        
        if (element.classList.contains('cart-count-bubble')) {
          if (cartData.item_count === 0) {
            element.classList.add('hide');
          } else {
            element.classList.remove('hide');
          }
        }
        // Add update animation
        element.classList.add('cart-element-updated');
        setTimeout(() => element.classList.remove('cart-element-updated'), 300);

        this.container.innerHTML = updatedCart.innerHTML;
        
        // After replacing HTML from sections, enforce disabled state for _for items using cart data
        if (cartData && cartData.items) {
          console.log('MiniCart: Enforcing disabled state after section update');
          setTimeout(() => {
            this.enforceDisabledStateForItems(cartData.items);
          }, 50); // Small delay to ensure DOM is ready
        }
      });
      
    } else {
      // Close after 4s if cart becomes empty while visible
      // A/B Testing Variant 1: Don't auto-hide even when cart becomes empty
      // Check dynamically in case variant is set after initialization
      if (cartData.item_count === 0 && this.isVisible && this.getAbTestVariant() !== 'variant1') {
        this.clearAutoHideTimeout();
        this.autoHideTimeout = setTimeout(() => this.hide(), 4000);
      }

      // Checkout button visibility
      if (cartData.item_count === 0) {
        this.hideCheckoutButton();
        // Ensure empty state is shown
        this.updateCartItems([]);
      } else {
        this.showCheckoutButton();
      }

      // Update checkout button state
      const checkoutButton = this.container.querySelector('[name="checkout"]');
      if (checkoutButton) {
        checkoutButton.disabled = cartData.item_count === 0;
      }


      // Mini cart count
      const countElements = this.container.querySelectorAll('[data-mini-cart-count]');
      countElements.forEach(element => {
        element.textContent = cartData.item_count || 0;
      });

      // Global cart count
      const globalCountElements = document.querySelectorAll('[data-cart-count]');
      console.log('[MiniCart] updateMiniCart updating global count', { count: cartData.item_count || 0, elements: globalCountElements.length });
      globalCountElements.forEach(element => {
        element.textContent = cartData.item_count || 0;
        element.setAttribute('data-count', cartData.item_count || 0);
        
        if (element.classList.contains('cart-count-bubble')) {
          if (cartData.item_count === 0) {
            element.classList.add('hide');
          } else {
            element.classList.remove('hide');
          }
        }
        
        // Add update animation
        element.classList.add('cart-element-updated');
        setTimeout(() => element.classList.remove('cart-element-updated'), 300);
      });

      // Subtotal and total
      const subtotalElements = this.container.querySelectorAll('[data-mini-cart-subtotal]');
      subtotalElements.forEach(element => {
        if (window.Shopify && window.Shopify.formatMoney) {
          element.textContent = window.Shopify.formatMoney(cartData.items_subtotal_price || 0);
        } else {
          element.textContent = this.formatMoney(cartData.items_subtotal_price || 0);
        }
      });

      const totalElements = this.container.querySelectorAll('[data-mini-cart-total]');
      totalElements.forEach(element => {
        if (window.Shopify && window.Shopify.formatMoney) {
          element.textContent = window.Shopify.formatMoney(cartData.total_price || 0);
        } else {
          element.textContent = this.formatMoney(cartData.total_price || 0);
        }
      });

      // Update cart items with the latest cart data
      // Always update to reflect any linked item quantity changes from _with items
      this.updateCartItems(cartData.items || []);
      
      // Enforce disabled state for items with _for property
      if (cartData && cartData.items) {
        this.enforceDisabledStateForItems(cartData.items);
      }
    }
  }

  /**
   * Enforce disabled state for items with _for property after DOM updates
   * @param {Array} items - Cart items array
   */
  enforceDisabledStateForItems(items) {
    if (!items || !Array.isArray(items)) return;
    
    items.forEach(item => {
      const itemElement = this.container.querySelector(`[data-item-key="${item.key}"]`);
      if (!itemElement) return;
      
      // Check if item has _for property
      let hasForProperty = false;
      try {
        if (item.properties) {
          if (Array.isArray(item.properties)) {
            hasForProperty = item.properties.some(prop => prop && prop.name === '_for' && prop.value);
          } else if (typeof item.properties === 'object') {
            hasForProperty = !!item.properties._for;
          }
        }
      } catch (error) {
        console.warn('MiniCart: Error checking _for property in enforceDisabledState:', error);
      }
      
      if (hasForProperty) {
        console.log('MiniCart: Enforcing disabled state for item with _for:', item.key);
        
        // Disable quantity input
        const quantityInput = itemElement.querySelector('[data-quantity-input]');
        if (quantityInput) {
          quantityInput.disabled = true;
          quantityInput.readOnly = true;
          quantityInput.style.pointerEvents = 'none';
          quantityInput.style.cursor = 'not-allowed';
          quantityInput.style.opacity = '0.7';
          quantityInput.style.backgroundColor = '#f3f4f6';
        }
        
        // Disable quantity buttons
        const decreaseBtn = itemElement.querySelector('[data-quantity-button="decrease"]');
        const increaseBtn = itemElement.querySelector('[data-quantity-button="increase"]');
        
        if (decreaseBtn) {
          decreaseBtn.disabled = true;
          decreaseBtn.style.opacity = '0.5';
          decreaseBtn.style.cursor = 'not-allowed';
          decreaseBtn.style.pointerEvents = 'none';
        }
        
        if (increaseBtn) {
          increaseBtn.disabled = true;
          increaseBtn.style.opacity = '0.5';
          increaseBtn.style.cursor = 'not-allowed';
          increaseBtn.style.pointerEvents = 'none';
        }
      }
    });
  }

  updateCartItems(items) {
    const itemsContainer = this.container.querySelector('[data-mini-cart-items]');
    if (!itemsContainer) return;

    if (!Array.isArray(items)) {
      console.warn('MiniCart: updateCartItems received non-array items:', items);
      items = [];
    }

    const itemKeys = new Set(items.map(i => i?.key).filter(Boolean));
    [this.pendingChanges, this.debounceTimers, this.lastCommittedQty].forEach(map => {
      map.forEach((_, key) => {
        if (!itemKeys.has(key)) map.delete(key);
      });
    });
    this.updatingItems.forEach(key => {
      if (!itemKeys.has(key)) this.updatingItems.delete(key);
    });

    if (items.length === 0) {
      itemsContainer.innerHTML = `
        <div class="mini-cart__empty">
          <p>Your cart is empty</p>
        </div>
      `;
      return;
    }

    // Render items
    let itemsHTML = '';
    
    items.forEach((item) => {
      try {
        // Safe access & fallbacks
        const itemKey = item.key || '';
        const itemImage = item.image || '';
        const itemTitle = item.product_title || (item.product?.title) || 'Product';
        const itemUrl = item.url || (item.product?.url) || '#';
        const variantTitle = item.variant_title || (item.variant?.title) || '';
        const finalPrice = item.final_price || 0;
        const quantity = item.quantity || 0;
        
        // Check if item has _for property (linked to main product)
        // Handle both object and array formats for properties
        let hasForProperty = false;
        try {
          if (item.properties) {
            if (Array.isArray(item.properties)) {
              hasForProperty = item.properties.some(prop => prop && prop.name === '_for' && prop.value);
              if (hasForProperty) {
                console.log('MiniCart: Found _for property (array format) for item:', itemKey, item.properties);
              }
            } else if (typeof item.properties === 'object') {
              hasForProperty = !!item.properties._for;
              if (hasForProperty) {
                console.log('MiniCart: Found _for property (object format) for item:', itemKey, item.properties._for);
              }
            }
          }
        } catch (error) {
          console.warn('MiniCart: Error checking _for property:', error);
          hasForProperty = false;
        }
        
        // Debug: Log all items to see their properties format
        if (items.indexOf(item) === 0) {
          console.log('MiniCart: Sample item properties structure:', {
            itemKey,
            hasProperties: !!item.properties,
            propertiesType: item.properties ? (Array.isArray(item.properties) ? 'array' : typeof item.properties) : 'none',
            properties: item.properties,
            hasForProperty
          });
        }
        
        // Track last committed qty for comparison
        this.lastCommittedQty.set(itemKey, quantity);
        
        // Use server quantity only; do not add pending here or we double-count after cart:updated (server 2 + pending 1 = 3)
        const displayQuantity = Math.max(0, quantity);
        
        itemsHTML += `
          <div class="mini-cart__item" data-item-key="${itemKey}">
            <div class="mini-cart__item-image">
              <a href="${itemUrl}" class="mini-cart__item-link" aria-label="View ${itemTitle}">
                ${itemImage ? `<img src="${itemImage}" alt="${itemTitle}" loading="lazy" class="w-full h-full object-cover rounded">` : ''}
              </a>
            </div>
            <div class="mini-cart__item-details">
              <div class="mini-cart__item-price">
                <span class="mini-cart__final-price">${this.formatMoney(finalPrice)}</span>
              </div>
              <h4 class="mini-cart__item-title">
                <a href="${itemUrl}" class="mini-cart__item-link">${itemTitle}</a>
              </h4>
              ${variantTitle && variantTitle !== 'Default Title' ? `<p class="mini-cart__item-variant">${variantTitle}</p>` : ''}

              <div class="mini-cart__item-quantity">
                <!-- Quantity controls (disabled if linked item with _for property) -->
                <button 
                  class="mini-cart__quantity-btn" 
                  data-quantity-button="decrease" 
                  data-item-key="${itemKey}"
                  aria-label="Decrease quantity"
                  ${hasForProperty ? 'disabled' : ''}
                  style="${hasForProperty ? 'opacity: 0.5; cursor: not-allowed;' : ''}"
                >
                  <span class="quantity-btn__text">
                    <svg fill="currentColor" width="14px" height="14px" viewBox="0 0 256 256" id="Flat" xmlns="http://www.w3.org/2000/svg">
                      <path d="M220,128a4.0002,4.0002,0,0,1-4,4H40a4,4,0,0,1,0-8H216A4.0002,4.0002,0,0,1,220,128Z"/>
                    </svg>
                  </span>
                  <span class="quantity-btn__loading hidden">
                    <svg class="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                </button>

                <input 
                  type="number" 
                  class="mini-cart__quantity-display" 
                  value="${displayQuantity}"
                  min="0"
                  data-quantity-input
                  data-item-key="${itemKey}"
                  data-original-quantity="${quantity}"
                  aria-label="Quantity"
                  ${hasForProperty ? 'disabled readonly' : ''}
                  style="${hasForProperty ? 'opacity: 0.7; cursor: not-allowed; pointer-events: none; background-color: #f3f4f6;' : ''}"
                >

                <button 
                  class="mini-cart__quantity-btn" 
                  data-quantity-button="increase" 
                  data-item-key="${itemKey}"
                  aria-label="Increase quantity"
                  ${hasForProperty ? 'disabled' : ''}
                  style="${hasForProperty ? 'opacity: 0.5; cursor: not-allowed;' : ''}"
                >
                  <span class="quantity-btn__text">
                    <svg fill="currentColor" width="14px" height="14px" viewBox="0 0 256 256" id="Flat" xmlns="http://www.w3.org/2000/svg">
                      <path d="M220,128a4.0002,4.0002,0,0,1-4,4H132v84a4,4,0,0,1-8,0V132H40a4,4,0,0,1,0-8h84V40a4,4,0,0,1,8,0v84h84A4.0002,4.0002,0,0,1,220,128Z"/>
                    </svg>
                  </span>
                  <span class="quantity-btn__loading hidden">
                    <svg class="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </div>
        `;
      } catch (error) {
        console.error('MiniCart: Error rendering item:', error);
      }
    });

    itemsContainer.innerHTML = itemsHTML;
  }

  // Update individual item quantity display
  updateItemQuantity(itemKey, newQuantity) {
    const itemElement = this.container.querySelector(`[data-item-key="${itemKey}"]`);
    if (!itemElement) return;

    const quantityDisplay = itemElement.querySelector('.mini-cart__quantity-display');
    if (quantityDisplay) {
      if (quantityDisplay.tagName === 'INPUT') {
        quantityDisplay.value = newQuantity;
        // Update the data-original-quantity attribute for button click functionality
        quantityDisplay.dataset.originalQuantity = newQuantity.toString();
      } else {
        quantityDisplay.textContent = newQuantity;
      }
    }
    // Update last committed map
    this.lastCommittedQty.set(itemKey, newQuantity);
  }

  // Remove individual item from display
  removeItemFromDisplay(itemKey) {
    const itemElement = this.container.querySelector(`[data-item-key="${itemKey}"]`);
    if (itemElement) {
      itemElement.remove();
      
      // Check if cart is now empty
      const remainingItems = this.container.querySelectorAll('[data-item-key]');
      if (remainingItems.length === 0) {
        console.log('MiniCart: Last item removed, showing empty state');
        this.handleEmptyCart();
      }
    }
    this.lastCommittedQty.delete(itemKey);
  }

  handleCartActions(event) {
    const target = event.target;
    
    // Find the closest quantity button (handles clicks on child elements like SVG, span)
    const quantityButton = target.closest('[data-quantity-button]');
    if (!quantityButton) return;
    
    // Skip if button is disabled (linked items with _for property)
    if (quantityButton.disabled) {
      return;
    }

    const action = quantityButton.dataset.quantityButton;
    const itemKey = quantityButton.dataset.itemKey;

    if (!itemKey) return;

    // Use the unified cart system if available
    if (window.cartManager) {
      this.handleQuantityButtonClick(action, itemKey);
    } else {
      console.warn('MiniCart: Unified cart system not available');
    }
  }

  /**
   * Handle manual edits in the quantity input.
   * commit=false: debounce and only update if value differs from original
   * commit=true: flush immediately (blur/Enter/change)
   */
  handleQuantityInputChange(event, { commit }) {
    const target = event.target;
    if (!target.matches('[data-quantity-input]')) return;
    
    // Skip if input is disabled (linked items with _for property)
    if (target.disabled || target.readOnly) {
      return;
    }

    const itemKey = target.dataset.itemKey;
    let newQuantity = parseInt(target.value, 10);

    // Clamp and sanitize (allow zero to remove)
    if (isNaN(newQuantity) || newQuantity < 0) newQuantity = 0;

    // Compare with last committed/known original to avoid redundant API calls
    const original = parseInt(target.dataset.originalQuantity || '0', 10) || 0;
    const lastCommitted = this.lastCommittedQty.get(itemKey) ?? original;

    // If nothing changed AND we're in commit step, just normalize UI and bail
    if (newQuantity === lastCommitted) {
      target.value = String(newQuantity);
      return;
    }

    // Update the UI value (so the field shows clamped value)
    target.value = String(newQuantity);

    if (!window.cartManager) {
      console.warn('MiniCart: Unified cart system not available');
      return;
    }

    if (commit) {
      // Flush immediately
      this.handleQuantityInputUpdate(itemKey, newQuantity);
      return;
    }

    // Debounced path while typing
    this.handleQuantityInputUpdate(itemKey, newQuantity);
  }

  handleQuantityButtonClick(action, itemKey) {
    // Update pending changes (no throttle: allow multiple + clicks to reach goal; one debounced request later)
    const currentPending = this.pendingChanges.get(itemKey) || 0;
    const newPending = currentPending + (action === 'increase' ? 1 : -1);
    this.pendingChanges.set(itemKey, newPending);
    
    // Update quantity display immediately for better UX
    this.updateQuantityDisplay(itemKey, newPending);
    
    // Clear existing timer for this item
    if (this.debounceTimers.has(itemKey)) {
      clearTimeout(this.debounceTimers.get(itemKey));
    }
    
    // Set new timer to update cart after 600ms
    const timer = setTimeout(() => {
      this.debouncedCartUpdate(itemKey);
    }, 600);
    
    this.debounceTimers.set(itemKey, timer);
  }

  handleQuantityInputUpdate(itemKey, newQuantity) {
    // Clear existing timer for this item
    if (this.debounceTimers.has(itemKey)) {
      clearTimeout(this.debounceTimers.get(itemKey));
    }
    
    // Set new timer to update cart after 400ms
    const timer = setTimeout(() => {
      this.debouncedCartInputUpdate(itemKey, newQuantity);
    }, 400);
    
    this.debounceTimers.set(itemKey, timer);
  }
  
  updateQuantityDisplay(itemKey, pendingChange) {
    const itemElement = this.container.querySelector(`[data-item-key="${itemKey}"]`);
    if (!itemElement) return;
    
    // Clear auto-hide timeout when updating quantity display
    this.clearAutoHideTimeout();
    
    const quantityDisplay = itemElement.querySelector('.mini-cart__quantity-display');
    if (quantityDisplay) {
      // Base must be last known server quantity so we don't use optimistic value as base (would cause +1 to show as +2)
      let originalQuantity = parseInt(quantityDisplay.dataset.originalQuantity, 10);
      if (Number.isNaN(originalQuantity)) {
        originalQuantity = parseInt(quantityDisplay.value || quantityDisplay.textContent, 10) || 0;
        quantityDisplay.dataset.originalQuantity = String(originalQuantity);
      }
      const newQuantity = Math.max(0, originalQuantity + pendingChange);
      
      if (quantityDisplay.tagName === 'INPUT') {
        quantityDisplay.value = newQuantity;
      } else {
        quantityDisplay.textContent = newQuantity;
      }
    }
  }
  
  async debouncedCartUpdate(itemKey) {
    // Use per-item updating state instead of global state
    if (this.updatingItems.has(itemKey)) {
      // If this specific item is already updating, reschedule this update
      setTimeout(() => this.debouncedCartUpdate(itemKey), 100);
      return;
    }

    // Clear auto-hide timeout when cart update starts
    this.clearAutoHideTimeout();
    this.updatingItems.add(itemKey);
    
    // Show loading state for this item
    const itemElement = this.container.querySelector(`[data-item-key="${itemKey}"]`);
    if (itemElement) {
      const buttons = itemElement.querySelectorAll('[data-quantity-button]');
      buttons.forEach(button => {
        const textSpan = button.querySelector('.quantity-btn__text');
        const loadingSpan = button.querySelector('.quantity-btn__loading');
        if (textSpan && loadingSpan) {
          textSpan.classList.add('hidden');
          loadingSpan.classList.remove('hidden');
        }
        button.disabled = true;
        button.style.pointerEvents = 'none';
        button.style.cursor = 'not-allowed';
      });
    }
    
    try {
      const cartManager = window.cartManager;
      const pendingChange = this.pendingChanges.get(itemKey) || 0;
      
      // Get current cart to find the item with adaptive timeout handling
      let cart;
      const startTime = performance.now();
      try {
        cart = await Promise.race([
          cartManager.getCart(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Cart fetch timeout')), this.getAdaptiveTimeout())
          )
        ]);
        
        // Track response time for connection health monitoring
        const responseTime = performance.now() - startTime;
        this.updateConnectionHealth(responseTime);
        
      } catch (error) {
        console.error('MiniCart: Failed to fetch cart', error);
        // Fallback: use displayed quantity and cart from manager if available
        const fallbackElement = this.container.querySelector(`[data-item-key="${itemKey}"]`);
        const quantityDisplay = fallbackElement?.querySelector('.mini-cart__quantity-display');
        const targetQuantity = quantityDisplay
          ? Math.max(0, parseInt(quantityDisplay.value || quantityDisplay.textContent, 10) || 0)
          : Math.max(0, pendingChange);
        const fallbackCart = window.cartManager?.cart;
        const fallbackItem = fallbackCart?.items?.find(it => it.key === itemKey);
        if (fallbackItem && targetQuantity >= 0) {
          const result = await cartManager.changeItemQuantity(fallbackItem.variant_id, targetQuantity);
          if (result) this.updateMiniCart(result);
        }
        return;
      }
      
      const item = cart.items.find(it => it.key === itemKey);
      
      if (!item) {
        console.warn('MiniCart: Item not found in cart', itemKey);
        return;
      }

      // Use displayed quantity so we send what the user sees (avoids stacking/race: one request, accurate qty)
      const quantityDisplay = itemElement?.querySelector('.mini-cart__quantity-display');
      const targetQuantity = quantityDisplay
        ? Math.max(0, parseInt(quantityDisplay.value || quantityDisplay.textContent, 10) || 0)
        : Math.max(0, item.quantity + pendingChange);
      const result = await cartManager.changeItemQuantity(item.variant_id, targetQuantity);

      if (result) {
        // Check if item was removed by looking at the result cart
        const resultItem = result.items.find(it => it.key === itemKey);
        
        if (!resultItem) {
          // Item was removed, remove it from display
          this.removeItemFromDisplay(itemKey);
        } else {
          // Update quantity display for the specific item
          this.updateItemQuantity(itemKey, resultItem.quantity);
          
          // Add update animation
          const el = this.container.querySelector(`[data-item-key="${itemKey}"]`);
          if (el) {
            el.classList.add('cart-element-updated');
            setTimeout(() => {
              el.classList.remove('cart-element-updated');
            }, 300);
          }
        }
        
        // Update cart totals
        this.updateMiniCart(result);
        
        // Force update all items to ensure linked items reflect quantity changes
        // This is critical for _with -> _for quantity synchronization
        if (result && result.items) {
          console.log('MiniCart: Force updating all items after button click, including linked items');
          this.updateCartItems(result.items);
        }
      }
    } catch (error) {
      console.error('MiniCart: Action failed', error);
    } finally {
      // Clear pending changes for this item
      this.pendingChanges.delete(itemKey);
      this.debounceTimers.delete(itemKey);
      this.updatingItems.delete(itemKey);
      
      // Hide loading state and re-enable buttons (but not if they were disabled for _for property)
      if (itemElement) {
        const buttons = itemElement.querySelectorAll('[data-quantity-button]');
        const input = itemElement.querySelector('[data-quantity-input]');
        
        // Check if this item has _for property (should stay disabled)
        const shouldStayDisabled = input && (input.hasAttribute('data-has-for-property') || input.readOnly);
        
        buttons.forEach(button => {
          const textSpan = button.querySelector('.quantity-btn__text');
          const loadingSpan = button.querySelector('.quantity-btn__loading');
          if (textSpan && loadingSpan) {
            textSpan.classList.remove('hidden');
            loadingSpan.classList.add('hidden');
          }
          
          // Only re-enable if not a linked item with _for property
          if (!shouldStayDisabled) {
            button.disabled = false;
            button.style.pointerEvents = 'auto';
            button.style.cursor = 'pointer';
          }
        });
      }
      
    }
  }

  async debouncedCartInputUpdate(itemKey, newQuantity) {
    // Use per-item updating state instead of global state
    if (this.updatingItems.has(itemKey)) {
      // If this specific item is already updating, reschedule this update
      setTimeout(() => this.debouncedCartInputUpdate(itemKey, newQuantity), 100);
      return;
    }

    // Clear auto-hide timeout when cart input update starts
    this.clearAutoHideTimeout();
    this.updatingItems.add(itemKey);
    
    // Show loading state for this item
    const itemElement = this.container.querySelector(`[data-item-key="${itemKey}"]`);
    if (itemElement) {
      const buttons = itemElement.querySelectorAll('[data-quantity-button]');
      const input = itemElement.querySelector('[data-quantity-input]');
      
      buttons.forEach(button => {
        const textSpan = button.querySelector('.quantity-btn__text');
        const loadingSpan = button.querySelector('.quantity-btn__loading');
        if (textSpan && loadingSpan) {
          textSpan.classList.add('hidden');
          loadingSpan.classList.remove('hidden');
        }
        button.disabled = true;
        button.style.pointerEvents = 'none';
        button.style.cursor = 'not-allowed';
      });
      
      if (input) {
        input.disabled = true;
      }
    }
    
    try {
      const cartManager = window.cartManager;
      
      // Get current cart to find the item
      const cart = await cartManager.getCart();
      const item = cart.items.find(it => it.key === itemKey);
      
      if (!item) {
        console.warn('MiniCart: Item not found in cart', itemKey);
        return;
      }

      
      // Avoid redundant API call if no change (guard here too)
      if (newQuantity === item.quantity) return;

      const result = await cartManager.changeItemQuantity(item.variant_id, Math.max(0, newQuantity));

      if (result) {
        // Check if item was removed by looking at the result cart
        const resultItem = result.items.find(it => it.key === itemKey);
        
        if (!resultItem) {
          // Item was removed, remove it from display
          this.removeItemFromDisplay(itemKey);
        } else {
          // Update quantity display for the specific item
          this.updateItemQuantity(itemKey, resultItem.quantity);
          
          // Add update animation
          const el = this.container.querySelector(`[data-item-key="${itemKey}"]`);
          if (el) {
            el.classList.add('cart-element-updated');
            setTimeout(() => {
              el.classList.remove('cart-element-updated');
            }, 300);
          }
        }
        
        // Update cart totals - this will also update all items including linked ones
        this.updateMiniCart(result);
        
        // Force update all items to ensure linked items reflect quantity changes
        // This is critical for _with -> _for quantity synchronization
        if (result && result.items) {
          console.log('MiniCart: Force updating all items after quantity change, including linked items');
          this.updateCartItems(result.items);
        }

        // Sync data-original-quantity
        const input = itemElement?.querySelector('[data-quantity-input]');
        if (input && resultItem) {
          input.dataset.originalQuantity = String(resultItem.quantity);
        }
      }
    } catch (error) {
      console.error('MiniCart: Input update failed', error);
      // Rollback UI on error to last committed value
      const last = this.lastCommittedQty.get(itemKey) ?? 0;
      this.updateItemQuantity(itemKey, last);
    } finally {
      // Clear debounce timer for this item
      this.debounceTimers.delete(itemKey);
      this.updatingItems.delete(itemKey);
      
      // Hide loading state and re-enable buttons and input (but not if they were disabled for _for property)
      if (itemElement) {
        const buttons = itemElement.querySelectorAll('[data-quantity-button]');
        const input = itemElement.querySelector('[data-quantity-input]');
        
        // Check if this item has _for property (should stay disabled)
        const shouldStayDisabled = input && (input.hasAttribute('data-has-for-property') || input.readOnly);
        
        buttons.forEach(button => {
          const textSpan = button.querySelector('.quantity-btn__text');
          const loadingSpan = button.querySelector('.quantity-btn__loading');
          if (textSpan && loadingSpan) {
            textSpan.classList.remove('hidden');
            loadingSpan.classList.add('hidden');
          }
          
          // Only re-enable if not a linked item with _for property
          if (!shouldStayDisabled) {
            button.disabled = false;
            button.style.pointerEvents = 'auto';
            button.style.cursor = 'pointer';
          }
        });
        
        if (input && !shouldStayDisabled) {
          input.disabled = false;
        }
      }
    }
  }

  formatMoney(cents) {
    if (!cents || isNaN(cents)) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * Handle empty cart state
   */
  handleEmptyCart() {
    console.log('MiniCart: Handling empty cart state');
    
    // Update cart items to show empty state
    this.updateCartItems([]);
    
    // Hide checkout button
    this.hideCheckoutButton();
    
    // Update any cart state elements
    const cartStateElements = this.container.querySelectorAll('[data-cart-state]');
    cartStateElements.forEach(element => {
      element.setAttribute('data-empty', 'true');
      element.setAttribute('data-item-count', '0');
      element.classList.add('cart-empty');
      element.classList.remove('cart-has-items');
    });
    
    // Clear any pending changes
    this.pendingChanges.clear();
    this.debounceTimers.clear();
    this.updatingItems.clear();
    this.lastCommittedQty.clear();
  }

  /**
   * Monitor connection health and adjust behavior
   */
  updateConnectionHealth(responseTime) {
    this.connectionHealth.lastResponseTime = responseTime;
    
    if (responseTime > 5000) { // Consider slow if > 5 seconds
      this.connectionHealth.slowResponseCount++;
      this.connectionHealth.isSlowConnection = this.connectionHealth.slowResponseCount >= 2;
      
      if (this.connectionHealth.isSlowConnection) {
        console.warn('MiniCart: Slow connection detected, adjusting behavior', {
          responseTime,
          slowResponseCount: this.connectionHealth.slowResponseCount
        });
      }
    } else {
      // Reset slow connection detection on fast responses
      this.connectionHealth.slowResponseCount = Math.max(0, this.connectionHealth.slowResponseCount - 1);
      this.connectionHealth.isSlowConnection = false;
    }
  }

  /**
   * Get appropriate timeout based on connection health
   */
  getAdaptiveTimeout() {
    return this.connectionHealth.isSlowConnection ? 25000 : 15000;
  }

  destroy() {
    this.clearShowTimeout();
    this.clearHideTimeout();
    
    // Remove document click and touch listeners (with capture flag)
    document.removeEventListener('click', this.handleClickOutside, true);
    document.removeEventListener('touchstart', this.handleClickOutside, true);
    document.body.removeEventListener('click', this.handleClickOutside, true);
    document.body.removeEventListener('touchstart', this.handleClickOutside, true);
    
    if (this.container) {
      this.container.removeEventListener('mouseenter', this.handleMouseEnter);
      this.container.removeEventListener('mouseleave', this.handleMouseLeave);
    }
    
    if (this.cartIcon) {
      this.cartIcon.removeEventListener('mouseenter', this.handleMouseEnter);
      this.cartIcon.removeEventListener('mouseleave', this.handleMouseLeave);
    }
    
    console.log('MiniCart: Destroyed');
  }

  /**
   * Disable mini cart functionality (used when on cart page)
   */
  disable() {
    this.isDisabled = true;
    
    // Remove event listeners
    document.removeEventListener('click', this.handleClickOutside, true);
    document.removeEventListener('touchstart', this.handleClickOutside, true);
    document.body.removeEventListener('click', this.handleClickOutside, true);
    document.body.removeEventListener('touchstart', this.handleClickOutside, true);
    
    if (this.container) {
      this.container.removeEventListener('mouseenter', this.clearHideTimeout);
      this.container.removeEventListener('mouseleave', this.hideOnHover);
      this.container.removeEventListener('click', this.handleCartActions);
    }
    
    // Hide the mini cart
    this.hide();
    
    console.log('MiniCart: Disabled');
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Wait a bit for the header to render and settings to be available
  setTimeout(() => {
    const cartContainers = Array.from(document.querySelectorAll('[data-cart-container]'));
    const eligibleContainers = cartContainers.filter((container) => (
      container.dataset.miniCartEnabled === 'true' &&
      !!container.querySelector('[data-mini-cart]')
    ));

    if (!eligibleContainers.length) {
      console.log('MiniCart: Skipping initialization - mini cart disabled in header settings');
      return;
    }

    const miniCartInstances = eligibleContainers
      .map((container) => {
        const cartToggleButtons = container.querySelectorAll('[data-cart-toggle]');
        const cartLinks = container.querySelectorAll('[data-cart-link]');

        if (!cartToggleButtons.length && !cartLinks.length) {
          return null;
        }

        return new MiniCart(container);
      })
      .filter(Boolean);

    if (!miniCartInstances.length) {
      console.log('MiniCart: Skipping initialization - mini cart container or cart icon not found');
      return;
    }

    window.miniCart = miniCartInstances[0];
    window.miniCarts = miniCartInstances;
  }, 100); // Small delay to ensure header is rendered
});

// Export for manual initialization
export default MiniCart;
